/**
 * Team-Übersicht für Chef/Filialleitung (Manager/Admin).
 *
 * Zeigt pro Mitarbeiter Stunden, Lohn und offen/abgerechnet im gewählten
 * Zeitraum (Server-Report /api/reports/team). Funktionen:
 *   - Zeitraum je Mitarbeiter abrechnen (Bulk-Settle).
 *   - Detail je Mitarbeiter aufklappen (Schichten des Zeitraums).
 *   - Urlaubs-/Frei-Anträge des Teams genehmigen/ablehnen.
 *   - CSV-Export der Team-Summen.
 */

import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../store/auth';
import { usePeriod } from '../store/period';
import { monthLabel } from '../lib/month';
import { apiFetch } from '../lib/api';
import { shiftsApi } from '../lib/shifts';
import {
  calendarEntriesApi,
  ENTRY_TYPE_META,
  needsApproval,
  type ApiCalendarEntry,
} from '../lib/calendarEntries';
import { usersApi } from '../lib/users';
import { companiesApi } from '../lib/companies';
import { formatDate, formatMoney } from '../lib/format';
import { shiftHours } from '../lib/payroll';
import { hapticError, hapticSuccess } from '../lib/haptics';
import { AdvanceDecisionsCard } from '../components/AdvanceDecisionsCard';
import { Avatar } from '../components/Avatar';
import { locationsApi, type ApiLocation } from '../lib/locations';
import { regionsApi } from '../lib/regions';

interface TeamRow {
  userId: string;
  name: string;
  role: 'admin' | 'manager' | 'employee';
  /** `null`, wenn `wageHidden` (Phase G) — kein Betrag für diese Zeile sichtbar. */
  hourlyWage: number | null;
  salaryMode: 'hourly' | 'salary';
  /** Abrechnungszeitraum dieser Person (Shadow 11.09.2026: nach ihrer Einstellung). */
  periodFrom?: string;
  periodTo?: string;
  /** Gesamtprüfung 11.09.2026 — optional für ältere Server. */
  breakDeductionEnabled?: boolean;
  hours: number;
  wage: number;
  open: number;
  settled: number;
  shiftsCount: number;
  /**
   * Phase G (Entscheidung #6): true, wenn ein Teil (oder alle) der Stunden
   * dieser Zeile aus einem Standort ohne `managerCanSeeWages` stammen — dann
   * sind `wage`/`open`/`settled` nur der sichtbare TEIL, nicht die volle Summe.
   * Für Owner/Admin immer false.
   */
  wageHidden: boolean;
  /** Screen 11: Summen dieser Person je Standort (Lohn null = dort nicht freigegeben). */
  byLocation?: TeamLocationPart[];
}
interface TeamLocationPart {
  locationId: string;
  hours: number;
  wage: number | null;
  open: number | null;
  settled: number | null;
  shiftsCount: number;
}
/** Screen 11 (Design-Canvas „Chef Lohnrechnung firmenweit"): Gruppierung der Team-Liste. */
type GroupBy = 'person' | 'location' | 'region';
interface LocationGroup {
  locationId: string;
  name: string;
  color: string | null;
  regionId: string | null;
  people: { userId: string; name: string; role: string; part: TeamLocationPart }[];
  hours: number;
  shiftsCount: number;
  /** Summe der sichtbaren Beträge; `wageHidden` = mind. ein Teil nicht freigegeben. */
  wage: number;
  open: number;
  wageHidden: boolean;
}
interface RegionGroup {
  regionId: string | null;
  name: string;
  locations: LocationGroup[];
  hours: number;
  shiftsCount: number;
  wage: number;
  open: number;
  wageHidden: boolean;
}
interface TeamTotals {
  hours: number;
  wage: number;
  open: number;
  settled: number;
  shiftsCount: number;
}
interface TeamReport {
  from: string;
  to: string;
  rows: TeamRow[];
  totals: TeamTotals;
  /** 'company' = Owner-Sicht (alle Standorte), 'ownLocations' = Filialleiter-Sicht (nur eigene). */
  scope: 'company' | 'ownLocations';
}

const fmtH = (h: number) => `${h.toFixed(2).replace('.', ',')} h`;
const roleLabel = (r: string) => (r === 'admin' ? 'Admin' : r === 'manager' ? 'Filialleitung' : 'Mitarbeiter');

export default function TeamOverviewPage() {
  const token = useAuth((s) => s.token)!;
  const activeCompanyId = useAuth((s) => s.activeCompanyId);
  const qc = useQueryClient();
  const month = usePeriod((s) => s.anchorMonth);
  const prevMonth = usePeriod((s) => s.prev);
  const nextMonth = usePeriod((s) => s.next);

  const { from, to } = useMemo(() => {
    const [y, m] = month.split('-').map(Number);
    return {
      from: `${month}-01`,
      to: new Date(Date.UTC(y!, m!, 0)).toISOString().slice(0, 10),
    };
  }, [month]);

  const [busyUser, setBusyUser] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [groupBy, setGroupBy] = useState<GroupBy>('person');

  const { data: report, isLoading } = useQuery({
    // Shadow 11.09.2026: „abgerechnet wird nach Einstellung“ — der Server
    // rechnet je Person ihren eigenen Abrechnungszeitraum, der in diesem Monat
    // beginnt (billingCycleStartDay), statt stur den Kalendermonat.
    queryKey: ['report', 'team', activeCompanyId, month],
    enabled: !!activeCompanyId,
    queryFn: () => apiFetch<TeamReport>(`/api/reports/team?month=${month}`, { token }),
  });

  const { data: users } = useQuery({ queryKey: ['users', activeCompanyId], queryFn: () => usersApi.list(token) });
  const nameById = useMemo(() => new Map((users ?? []).map((u) => [u.id, u.name])), [users]);
  const { data: companies } = useQuery({ queryKey: ['companies'], queryFn: () => companiesApi.list(token) });
  const companyName = companies?.find((c) => c.id === activeCompanyId)?.name;

  // Screen 11: Standorte/Regionen für die Gruppierung (nur geladen, wenn gebraucht).
  const { data: locations } = useQuery({
    queryKey: ['locations', activeCompanyId],
    enabled: !!activeCompanyId && groupBy !== 'person',
    queryFn: () => locationsApi.list(token),
  });
  const { data: regions } = useQuery({
    queryKey: ['regions', activeCompanyId],
    enabled: !!activeCompanyId && groupBy === 'region',
    queryFn: () => regionsApi.list(token),
  });
  const locationGroups = useMemo(
    () => buildLocationGroups(report?.rows ?? [], locations ?? []),
    [report, locations],
  );
  const regionGroups = useMemo(() => {
    const regionName = new Map<string, string>((regions ?? []).map((r) => [r.id, r.name] as [string, string]));
    const byRegion = new Map<string | null, LocationGroup[]>();
    for (const g of locationGroups) {
      const key = g.regionId && regionName.has(g.regionId) ? g.regionId : null;
      byRegion.set(key, [...(byRegion.get(key) ?? []), g]);
    }
    const out: RegionGroup[] = [...byRegion.entries()].map(([regionId, locs]) => ({
      regionId,
      name: regionId ? regionName.get(regionId) ?? 'Region' : 'Ohne Region',
      locations: locs,
      hours: locs.reduce((a, l) => a + l.hours, 0),
      shiftsCount: locs.reduce((a, l) => a + l.shiftsCount, 0),
      wage: locs.reduce((a, l) => a + l.wage, 0),
      open: locs.reduce((a, l) => a + l.open, 0),
      wageHidden: locs.some((l) => l.wageHidden),
    }));
    // Regionen alphabetisch, "Ohne Region" zuletzt.
    return out.sort((a, b) => (a.regionId === null ? 1 : b.regionId === null ? -1 : a.name.localeCompare(b.name, 'de')));
  }, [locationGroups, regions]);

  const { data: pendingEntries } = useQuery({
    queryKey: ['calendar-entries', 'pending', activeCompanyId],
    enabled: !!activeCompanyId,
    queryFn: () => calendarEntriesApi.list(token, { status: 'pending' }),
  });
  const pendingAbsences = useMemo(
    () => (pendingEntries ?? []).filter((e) => needsApproval(e.type) && !e.deletedAt),
    [pendingEntries],
  );

  async function settleUser(row: TeamRow) {
    if (busyUser) return;
    setBusyUser(row.userId);
    setFeedback(null);
    try {
      const shifts = await shiftsApi.list(token, {
        from: row.periodFrom ?? from,
        to: row.periodTo ?? to,
        userId: row.userId,
      });
      const openIds = shifts
        .filter((s) => s.status !== 'cancelled' && !s.settledAt && !s.deletedAt)
        .map((s) => s.id);
      if (openIds.length === 0) {
        setFeedback(`${row.name}: keine offenen Schichten.`);
        return;
      }
      const res = await apiFetch<{ updated: number }>('/api/shifts/settle', {
        method: 'POST',
        body: { ids: openIds, settled: true },
        token,
      });
      hapticSuccess();
      setFeedback(`${row.name}: ${res.updated} Schicht(en) abgerechnet.`);
      await qc.invalidateQueries({ queryKey: ['report', 'team'] });
    } catch (err) {
      hapticError();
      setFeedback(err instanceof Error ? err.message : 'Abrechnen fehlgeschlagen');
    } finally {
      setBusyUser(null);
    }
  }

  async function decide(entry: ApiCalendarEntry, status: 'approved' | 'rejected') {
    try {
      await calendarEntriesApi.update(token, entry.id, { status }, entry.lastModified);
      hapticSuccess();
      await qc.invalidateQueries({ queryKey: ['calendar-entries', 'pending'] });
    } catch (err) {
      hapticError();
      setFeedback(err instanceof Error ? err.message : 'Aktion fehlgeschlagen');
    }
  }

  function exportCsv() {
    const rows = report?.rows ?? [];
    const n = (x: number) => x.toFixed(2).replace('.', ',');
    const header = ['Mitarbeiter', 'Rolle', 'Zeitraum von', 'Zeitraum bis', 'Schichten', 'Stunden', 'Lohn', 'offen', 'abgerechnet'];
    const lines = [header.join(';')];
    for (const r of rows) {
      lines.push(
        [
          r.name,
          roleLabel(r.role),
          r.periodFrom ?? from,
          r.periodTo ?? to,
          String(r.shiftsCount),
          n(r.hours),
          n(r.wage),
          n(r.open),
          n(r.settled),
        ].join(';'),
      );
    }
    const csv = '﻿' + lines.join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `team-${from}_bis_${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const totals = report?.totals;

  return (
    <div className="max-w-5xl mx-auto space-y-4 pb-10">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <h2 className="text-xl font-bold">Team-Übersicht</h2>
        <button onClick={exportCsv} className="btn-ghost text-xs px-3 py-1.5">
          ⇩ CSV-Export
        </button>
      </div>

      {/* Zeitraum */}
      <div className="flex items-center justify-between card p-3">
        <button onClick={prevMonth} className="btn-ghost px-3 py-1" aria-label="Vorheriger Monat">
          ‹
        </button>
        <div className="text-center">
          <p className="font-semibold">{monthLabel(month)}</p>
          <p className="text-[11px] text-brand-light">{companyName ?? 'Firma wählen'}</p>
          <p className="text-[10px] text-slate-500">je Person nach ihrem Abrechnungszeitraum</p>
          {totals && (
            <p className="text-xs text-slate-400">
              {totals.shiftsCount} Schichten · {fmtH(totals.hours)} ·{' '}
              <span className="text-emerald-400">{formatMoney(totals.wage)}</span>
            </p>
          )}
        </div>
        <button onClick={nextMonth} className="btn-ghost px-3 py-1" aria-label="Nächster Monat">
          ›
        </button>
      </div>

      {/* Summen */}
      {totals && (
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          <Kpi label="Stunden" value={fmtH(totals.hours)} />
          <Kpi label="Offen (Auszahlung)" value={formatMoney(totals.open)} highlight />
          <Kpi label="Abgerechnet" value={formatMoney(totals.settled)} />
        </div>
      )}

      {/* Urlaub / Frei genehmigen */}
      {pendingAbsences.length > 0 && (
        <section className="card p-4">
          <h3 className="text-sm uppercase tracking-wider text-slate-400 mb-3">
            Anträge ({pendingAbsences.length})
          </h3>
          <ul className="space-y-2">
            {pendingAbsences.map((e) => {
              const meta = ENTRY_TYPE_META[e.type];
              return (
                <li
                  key={e.id}
                  className="bg-slate-900/40 rounded p-3 flex items-center gap-3 border-l-4"
                  style={{ borderLeftColor: meta.color }}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">
                      {nameById.get(e.userId) ?? '?'} · <span style={{ color: meta.color }}>{meta.label}</span>
                      {e.halfDay && <span className="text-xs text-slate-400"> · halber Tag</span>}
                    </p>
                    <p className="text-xs text-slate-400">
                      {formatDate(e.date)}
                      {e.title ? ` · ${e.title}` : ''}
                      {e.note ? ` · „${e.note}"` : ''}
                    </p>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <button
                      onClick={() => decide(e, 'approved')}
                      className="text-xs bg-emerald-600/80 hover:bg-emerald-600 rounded px-2.5 py-1.5"
                    >
                      Freigeben
                    </button>
                    <button
                      onClick={() => decide(e, 'rejected')}
                      className="text-xs bg-red-600/80 hover:bg-red-600 rounded px-2.5 py-1.5"
                    >
                      Ablehnen
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <AdvanceDecisionsCard />

      {/* Screen 11: Gruppierung umschalten */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-xs uppercase tracking-wider text-slate-400">Gruppieren nach</p>
        <div className="inline-flex rounded-lg bg-slate-800 p-0.5" role="group" aria-label="Gruppieren nach">
          {(
            [
              ['person', 'Mitarbeiter'],
              ['location', 'Standort'],
              ['region', 'Region'],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setGroupBy(key)}
              aria-pressed={groupBy === key}
              className={`text-xs px-3 py-1.5 rounded-md !min-h-0 ${
                groupBy === key ? 'bg-slate-600 text-white' : 'text-slate-300 hover:text-white'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Mitarbeiter-Tabelle */}
      <div className="card overflow-hidden">
        {!activeCompanyId ? (
          <p className="p-4 text-sm text-slate-400">
            Bitte oben eine Firma wählen — die Team-Übersicht zeigt genau eine Firma.
          </p>
        ) : isLoading ? (
          <p className="p-4 text-sm text-slate-400">Lädt…</p>
        ) : !report || report.rows.length === 0 ? (
          <p className="p-4 text-sm text-slate-400">Keine Schichten im Zeitraum.</p>
        ) : groupBy === 'location' ? (
          <div className="divide-y divide-border/60">
            {locationGroups.map((g) => (
              <LocationSection key={g.locationId} group={g} />
            ))}
          </div>
        ) : groupBy === 'region' ? (
          <div className="divide-y divide-border/60">
            {regionGroups.map((rg) => (
              <section key={rg.regionId ?? 'none'}>
                <GroupHeader
                  title={rg.name}
                  subtitle={`${rg.locations.length} Standort${rg.locations.length === 1 ? '' : 'e'}`}
                  shiftsCount={rg.shiftsCount}
                  hours={rg.hours}
                  wage={rg.wage}
                  open={rg.open}
                  wageHidden={rg.wageHidden}
                />
                <ul className="divide-y divide-border/40">
                  {rg.locations.map((g) => (
                    <li key={g.locationId} className="px-3 py-2 flex items-center gap-3">
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: g.color ?? '#64748b' }}
                        aria-hidden
                      />
                      <span className="min-w-0 flex-1 truncate text-sm">
                        {g.name}{' '}
                        <span className="text-[11px] text-slate-500">· {g.people.length} Pers.</span>
                      </span>
                      <span className="text-xs text-slate-400 shrink-0 text-right">
                        {g.shiftsCount} Schichten · {fmtH(g.hours)} ·{' '}
                        <span className="text-emerald-400">{formatMoney(g.wage)}</span>
                        {g.wageHidden && <HiddenMark />}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        ) : (
          <ul className="divide-y divide-border/60">
            {report.rows.map((r) => (
              <li key={r.userId}>
                <div className="p-3 flex items-center gap-3">
                  <Avatar name={r.name} />
                  <button
                    onClick={() => setExpanded(expanded === r.userId ? null : r.userId)}
                    className="min-w-0 flex-1 text-left !min-h-0"
                  >
                    <p className="font-medium truncate">
                      {r.name}{' '}
                      <span className="text-[11px] text-slate-500">{roleLabel(r.role)}</span>
                    </p>
                    <p className="text-xs text-slate-400">
                      {r.shiftsCount} Schichten · {fmtH(r.hours)} ·{' '}
                      <span className="text-emerald-400">{formatMoney(r.wage)}</span>
                      {r.wageHidden && (
                        <span
                          className="ml-1 text-amber-300"
                          title="Lohn an mind. einem Standort für dich nicht freigegeben — Summe unvollständig"
                        >
                          *
                        </span>
                      )}
                    </p>
                    <p className="text-[11px] text-slate-500">
                      offen <span className="text-amber-300">{formatMoney(r.open)}</span> · abgerechnet{' '}
                      <span className="text-emerald-400">{formatMoney(r.settled)}</span>
                    </p>
                    {r.periodFrom && r.periodTo && (r.periodFrom !== from || r.periodTo !== to) && (
                      <p className="text-[11px] text-slate-500">
                        Zeitraum {formatDate(r.periodFrom)} – {formatDate(r.periodTo)}
                      </p>
                    )}
                  </button>
                  {r.open > 0 && (
                    <button
                      onClick={() => settleUser(r)}
                      disabled={busyUser === r.userId}
                      className="btn-ghost text-xs px-3 py-1.5 shrink-0 disabled:opacity-50"
                    >
                      {busyUser === r.userId ? '…' : 'Abrechnen'}
                    </button>
                  )}
                </div>
                {expanded === r.userId && (
                  <UserShiftDetail
                    userId={r.userId}
                    from={r.periodFrom ?? from}
                    to={r.periodTo ?? to}
                    breakDeduction={r.breakDeductionEnabled ?? false}
                  />
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {feedback && <p className="text-sm text-emerald-400">{feedback}</p>}

      {groupBy !== 'person' && (
        <p className="text-xs text-slate-500">
          Abrechnen je Mitarbeiter geht in der Ansicht „Mitarbeiter" (rechnet alle Standorte des Zeitraums ab).
        </p>
      )}

      <p className="text-xs text-slate-500">
        „Offen" = noch nicht abgerechnet (die künftige Auszahlung). Bei Festgehalt-Mitarbeitern
        ist der Lohn hier als Stunden × Satz gerechnet (Stunden sind exakt).
      </p>
    </div>
  );
}

function Kpi({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="card p-2 sm:p-3 min-w-0">
      <p className="text-[11px] sm:text-xs text-slate-400 truncate">{label}</p>
      <p className={`text-base sm:text-lg font-semibold mt-0.5 truncate ${highlight ? 'text-amber-300' : ''}`}>
        {value}
      </p>
    </div>
  );
}

function UserShiftDetail({
  userId,
  from,
  to,
  breakDeduction,
}: {
  userId: string;
  from: string;
  to: string;
  breakDeduction: boolean;
}) {
  const token = useAuth((s) => s.token)!;
  const activeCompanyId = useAuth((s) => s.activeCompanyId);
  const { data: shifts, isLoading } = useQuery({
    queryKey: ['shifts', 'user-range', activeCompanyId, userId, from, to],
    queryFn: () => shiftsApi.list(token, { userId, from, to }),
  });
  const list = (shifts ?? []).filter((s) => s.status !== 'cancelled' && !s.deletedAt);
  return (
    <div className="bg-slate-900/40 px-3 py-2">
      {isLoading ? (
        <p className="text-xs text-slate-500 py-1">Lädt…</p>
      ) : list.length === 0 ? (
        <p className="text-xs text-slate-500 py-1">Keine Schichten.</p>
      ) : (
        <ul className="space-y-1">
          {list.map((s) => {
            const h = shiftHours(s, { breakDeduction });
            return (
              <li key={s.id} className="text-xs flex items-center justify-between gap-2">
                <span className="text-slate-300">
                  {formatDate(s.shiftDate)} · {s.startTime}–{s.endTime}
                </span>
                <span className="text-slate-400">
                  {fmtH(h)} ·{' '}
                  <span className="text-emerald-400">
                    {s.hourlyWage == null ? '–' : formatMoney(h * s.hourlyWage)}
                  </span>
                  {s.settledAt ? (
                    <span className="ml-1 text-emerald-300/80">✓</span>
                  ) : (
                    <span className="ml-1 text-amber-300/80">offen</span>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Screen 11: Team-Zeilen je Standort gruppieren (Standorte alphabetisch, Personen nach Stunden). */
function buildLocationGroups(rows: TeamRow[], locations: ApiLocation[]): LocationGroup[] {
  const locById = new Map(locations.map((l) => [l.id, l]));
  const groups = new Map<string, LocationGroup>();
  for (const r of rows) {
    for (const part of r.byLocation ?? []) {
      let g = groups.get(part.locationId);
      if (!g) {
        const loc = locById.get(part.locationId);
        g = {
          locationId: part.locationId,
          name: loc?.name ?? 'Unbekannter Standort',
          color: loc?.color ?? null,
          regionId: loc?.regionId ?? null,
          people: [],
          hours: 0,
          shiftsCount: 0,
          wage: 0,
          open: 0,
          wageHidden: false,
        };
        groups.set(part.locationId, g);
      }
      g.people.push({ userId: r.userId, name: r.name, role: r.role, part });
      g.hours += part.hours;
      g.shiftsCount += part.shiftsCount;
      if (part.wage === null) g.wageHidden = true;
      else g.wage += part.wage;
      if (part.open !== null) g.open += part.open;
    }
  }
  const out = [...groups.values()];
  for (const g of out) g.people.sort((a, b) => b.part.hours - a.part.hours);
  return out.sort((a, b) => a.name.localeCompare(b.name, 'de'));
}

function HiddenMark() {
  return (
    <span className="ml-1 text-amber-300" title="Lohn an mind. einem Standort für dich nicht freigegeben — Summe unvollständig">
      *
    </span>
  );
}

function GroupHeader({
  title,
  subtitle,
  color,
  shiftsCount,
  hours,
  wage,
  open,
  wageHidden,
}: {
  title: string;
  subtitle?: string;
  color?: string | null;
  shiftsCount: number;
  hours: number;
  wage: number;
  open: number;
  wageHidden: boolean;
}) {
  return (
    <div className="px-3 py-2.5 bg-slate-900/50 flex items-center gap-3 flex-wrap">
      {color !== undefined && (
        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: color ?? '#64748b' }} aria-hidden />
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold truncate">{title}</p>
        {subtitle && <p className="text-[11px] text-slate-500">{subtitle}</p>}
      </div>
      <p className="text-xs text-slate-400 text-right">
        {shiftsCount} Schichten · {fmtH(hours)} · <span className="text-emerald-400">{formatMoney(wage)}</span>
        {wageHidden && <HiddenMark />}
        <br />
        <span className="text-[11px]">
          offen <span className="text-amber-300">{formatMoney(open)}</span>
        </span>
      </p>
    </div>
  );
}

function LocationSection({ group }: { group: LocationGroup }) {
  return (
    <section>
      <GroupHeader
        title={group.name}
        subtitle={`${group.people.length} Person${group.people.length === 1 ? '' : 'en'}`}
        color={group.color}
        shiftsCount={group.shiftsCount}
        hours={group.hours}
        wage={group.wage}
        open={group.open}
        wageHidden={group.wageHidden}
      />
      <ul className="divide-y divide-border/40">
        {group.people.map((p) => (
          <li key={p.userId} className="px-3 py-2 flex items-center gap-3">
            <Avatar name={p.name} />
            <span className="min-w-0 flex-1 truncate text-sm">
              {p.name} <span className="text-[11px] text-slate-500">{roleLabel(p.role)}</span>
            </span>
            <span className="text-xs text-slate-400 shrink-0 text-right">
              {p.part.shiftsCount} Schichten · {fmtH(p.part.hours)} ·{' '}
              {p.part.wage === null ? (
                <span className="text-slate-500">€ nicht freigegeben</span>
              ) : (
                <span className="text-emerald-400">{formatMoney(p.part.wage)}</span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
